import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { TrendingUp, TrendingDown, DollarSign, AlertCircle, CheckCircle, Calendar } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/config';

const API = `${BACKEND_URL}/api`;

const KPICashFlow = ({ filters }) => {
  const [cashFlowData, setCashFlowData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCashFlowData();
  }, [filters]);

  const fetchCashFlowData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      
      const params = new URLSearchParams({
        year: filters.year
      });
      
      if (filters.companyId) {
        params.append('company_id', filters.companyId);
      }
      
      const response = await axios.get(`${API}/kpi/cash-flow?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCashFlowData(response.data);
      
    } catch (error) {
      console.error('Error fetching cash flow data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !cashFlowData) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Prepare data for charts
  const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  
  const cashFlowChartData = Object.keys(cashFlowData.monthly_cash_in).map(month => ({
    month: monthNames[parseInt(month) - 1],
    monthNum: parseInt(month),
    entrate: cashFlowData.monthly_cash_in[month] || 0,
    uscite: cashFlowData.monthly_cash_out[month] || 0,
    netto: (cashFlowData.monthly_cash_in[month] || 0) - (cashFlowData.monthly_cash_out[month] || 0),
    cumulativo: cashFlowData.cumulative_cash[month] || 0
  }));

  const fabbisognoChartData = Object.keys(cashFlowData.cash_requirement_monthly).map(month => ({
    month: monthNames[parseInt(month) - 1],
    fabbisogno: cashFlowData.cash_requirement_monthly[month]
  }));

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, badge }) => (
    <Card className="p-6 bg-gradient-to-br from-white to-gray-50 shadow-lg hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            {badge && (
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${badge.color}`}>
                {badge.text}
              </span>
            )}
          </div>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              {trend.direction === 'up' ? (
                <TrendingUp className="h-4 w-4 text-green-600" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-600" />
              )}
              <span className={`text-sm font-medium ${trend.direction === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {trend.value}
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full bg-gradient-to-br ${color.replace('text-', 'from-')}-100 ${color.replace('text-', 'to-')}-200`}>
          <Icon className={`h-8 w-8 ${color}`} />
        </div>
      </div>
    </Card>
  );

  const breakEvenReached = cashFlowData.break_even_point_month !== null;
  const hasDeficit = cashFlowData.cash_requirement_annual > 0;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Cash Flow & Break-Even Analysis</h2>
        <p className="text-gray-600">Analisi del flusso di cassa e punto di pareggio per l'anno {cashFlowData.year}</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Fabbisogno Cassa Annuale"
          value={formatCurrency(cashFlowData.cash_requirement_annual)}
          subtitle="Massimo deficit nell'anno"
          icon={AlertCircle}
          color={hasDeficit ? "text-red-600" : "text-green-600"}
          badge={hasDeficit ? {text: "Deficit", color: "bg-red-100 text-red-800"} : {text: "Positivo", color: "bg-green-100 text-green-800"}}
        />
        
        <StatCard
          title="Break-Even Point"
          value={breakEvenReached ? `Mese ${cashFlowData.break_even_point_month}` : "Non raggiunto"}
          subtitle={breakEvenReached ? monthNames[cashFlowData.break_even_point_month - 1] : "Deficit persistente"}
          icon={breakEvenReached ? CheckCircle : AlertCircle}
          color={breakEvenReached ? "text-green-600" : "text-orange-600"}
          badge={breakEvenReached ? {text: "Raggiunto", color: "bg-green-100 text-green-800"} : {text: "In attesa", color: "bg-orange-100 text-orange-800"}}
        />
        
        <StatCard
          title="Mesi al Break-Even"
          value={cashFlowData.months_to_break_even ? cashFlowData.months_to_break_even.toFixed(1) : "N/A"}
          subtitle={cashFlowData.months_to_break_even ? "Tempo per pareggio" : "Non applicabile"}
          icon={Calendar}
          color="text-blue-600"
        />
        
        <StatCard
          title="Cash Flow Netto"
          value={formatCurrency(cashFlowData.net_cash_flow)}
          subtitle="Entrate - Uscite annuali"
          icon={cashFlowData.net_cash_flow >= 0 ? TrendingUp : TrendingDown}
          color={cashFlowData.net_cash_flow >= 0 ? "text-green-600" : "text-red-600"}
        />
      </div>

      {/* Cumulative Cash Flow Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-bold mb-4">Flusso di Cassa Cumulativo</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={cashFlowChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis 
              tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip 
              formatter={(value) => formatCurrency(value)}
              labelFormatter={(label) => `Mese: ${label}`}
            />
            <Legend />
            <ReferenceLine y={0} stroke="#000" strokeDasharray="3 3" />
            <Line 
              type="monotone" 
              dataKey="cumulativo" 
              stroke="#2563eb" 
              strokeWidth={3}
              name="Cassa Cumulativa"
              dot={{ fill: '#2563eb', r: 4 }}
            />
            {cashFlowData.break_even_point_month && (
              <ReferenceLine 
                x={monthNames[cashFlowData.break_even_point_month - 1]} 
                stroke="#16a34a" 
                strokeWidth={2}
                strokeDasharray="5 5"
                label={{ value: 'Break-Even', position: 'top', fill: '#16a34a', fontWeight: 'bold' }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Monthly Cash Flow Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-bold mb-4">Entrate vs Uscite Mensili</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={cashFlowChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis 
              tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip 
              formatter={(value) => formatCurrency(value)}
              labelFormatter={(label) => `Mese: ${label}`}
            />
            <Legend />
            <Bar dataKey="entrate" fill="#10b981" name="Entrate" />
            <Bar dataKey="uscite" fill="#ef4444" name="Uscite" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Cash Requirement by Month */}
      {fabbisognoChartData.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4">Fabbisogno di Cassa per Mese</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={fabbisognoChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis 
                tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                labelFormatter={(label) => `Mese: ${label}`}
              />
              <Bar dataKey="fabbisogno" fill="#f59e0b" name="Fabbisogno" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Detailed Monthly Table */}
      <Card className="p-6">
        <h3 className="text-lg font-bold mb-4">Dettaglio Mensile</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mese</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Entrate</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Uscite</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Flusso Netto</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cumulativo</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fabbisogno</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cashFlowChartData.map((row, idx) => {
                const fabbisogno = cashFlowData.cash_requirement_monthly[row.monthNum] || 0;
                const isBreakEven = row.monthNum === cashFlowData.break_even_point_month;
                
                return (
                  <tr key={idx} className={`hover:bg-gray-50 ${isBreakEven ? 'bg-green-50' : ''}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {row.month}
                      {isBreakEven && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Break-Even
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600 font-semibold">
                      {formatCurrency(row.entrate)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600 font-semibold">
                      {formatCurrency(row.uscite)}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-semibold ${row.netto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(row.netto)}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-bold ${row.cumulativo >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(row.cumulativo)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-orange-600 font-semibold">
                      {fabbisogno > 0 ? formatCurrency(fabbisogno) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-100 font-bold">
              <tr>
                <td className="px-4 py-3 text-sm text-gray-900">TOTALE</td>
                <td className="px-4 py-3 text-sm text-right text-green-700">
                  {formatCurrency(cashFlowData.total_cash_in)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-red-700">
                  {formatCurrency(cashFlowData.total_cash_out)}
                </td>
                <td className={`px-4 py-3 text-sm text-right ${cashFlowData.net_cash_flow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(cashFlowData.net_cash_flow)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-blue-700">
                  {formatCurrency(cashFlowData.cumulative_cash[12] || 0)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-orange-700">
                  {formatCurrency(cashFlowData.cash_requirement_annual)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Insights */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50">
        <h3 className="text-lg font-bold mb-3">📊 Insights</h3>
        <div className="space-y-2 text-sm text-gray-700">
          {hasDeficit ? (
            <>
              <p>💰 <strong>Fabbisogno di Cassa:</strong> Serve un capitale di <strong>{formatCurrency(cashFlowData.cash_requirement_annual)}</strong> per coprire i deficit mensili.</p>
              {breakEvenReached ? (
                <p>✅ <strong>Break-Even Raggiunto:</strong> Il pareggio viene raggiunto a <strong>{monthNames[cashFlowData.break_even_point_month - 1]}</strong> (mese {cashFlowData.break_even_point_month}).</p>
              ) : (
                <p>⚠️ <strong>Attenzione:</strong> Il break-even non viene raggiunto nell'anno. È necessario aumentare i ricavi o ridurre i costi.</p>
              )}
            </>
          ) : (
            <p>🎉 <strong>Eccellente!</strong> Il flusso di cassa è positivo per tutto l'anno. Non è necessario capitale aggiuntivo.</p>
          )}
          {cashFlowData.months_to_break_even && (
            <p>📅 <strong>Tempo al Pareggio:</strong> Circa <strong>{cashFlowData.months_to_break_even.toFixed(1)} mesi</strong> per raggiungere il break-even.</p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default KPICashFlow;
